import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormItem, Input } from '@packages/utils';
import { Select } from 'components/Base/Select';
import { RadioGroup, Radio } from 'components/Base/RadioGroup';
import { useAddCustomTacticMutation } from 'redux/apiSlice/pillarsActionPlan.slice';
import { useGetPillarsOfStrengthQuery } from 'redux/apiSlice/pillars.slice';
import { TAddCustomTacticRequest } from 'data/types/pillarsActionPlan.types';
import useForm from 'hooks/useForm';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import {
  StyledModal,
  ModalFooter,
  CancelButton,
  PrimaryButton,
  FormContainer,
} from 'components/Shared/StyledModal';
import { GoalLevelFields } from '../GoalLevelFields';
import {
  TAddTacticModalProps,
  TAddTacticFormValues,
} from './AddTacticModal.types';

const AddTacticModal: React.FC<TAddTacticModalProps> = ({
  open,
  onClose,
  onAdd,
  elcId,
}) => {
  const { form } = useForm<TAddTacticFormValues>();
  const { showToastNotification } = useShowToastNotification();
  const [addCustomTactic, { isLoading }] = useAddCustomTacticMutation();
  const [tacticNameError, setTacticNameError] = useState<string | null>(null);

  // Mirror pillarId and subPillarId in state so we can gate Goal/Level selects.
  // Form.useWatch is not available on the @packages/utils Form wrapper.
  const [selectedPillarId, setSelectedPillarId] = useState<
    string | undefined
  >();
  const [selectedSubPillarId, setSelectedSubPillarId] = useState<
    string | undefined
  >();

  // The BE tolerates a blank elcId on this endpoint (returns the tree with no
  // per-ELC statuses), so the All-ELCs page — which has no ELC — still gets
  // the pillar/goal/level hierarchy needed for the cascade.
  const {
    data: pillarsOfStrength,
    isError: pillarsOfStrengthError,
    error: pillarsOfStrengthErrorPayload,
  } = useGetPillarsOfStrengthQuery({ elcId: elcId ?? '' }, { skip: !open });

  const pillarsErrorToastShown = useRef(false);

  useEffect(() => {
    if (!open) {
      pillarsErrorToastShown.current = false;
      return;
    }
    if (pillarsOfStrengthError && !pillarsErrorToastShown.current) {
      pillarsErrorToastShown.current = true;
      const message =
        (pillarsOfStrengthErrorPayload as any)?.data?.message ||
        'Unable to load Pillar, Goal, and Level options. Please try again or contact support.';
      showToastNotification({
        type: ENotificationType.ERROR,
        message,
      });
    }
  }, [
    open,
    pillarsOfStrengthError,
    pillarsOfStrengthErrorPayload,
    showToastNotification,
  ]);

  const pillarsOptions = useMemo(
    () =>
      pillarsOfStrength?.pillars.map(p => ({ label: p.name, value: p.id })) ??
      [],
    [pillarsOfStrength],
  );

  const handlePillarChange = () => {
    form.setFieldsValue({ subPillarId: undefined, levelId: undefined });
    setSelectedSubPillarId(undefined);
  };

  const handleSubPillarChange = () => {
    form.setFieldsValue({ levelId: undefined });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const requestBody: TAddCustomTacticRequest = {
        title: values.tacticName,
        pillarsId: values.pillarId,
      };

      if (values.scope) {
        requestBody.scope = values.scope;
      } else if (elcId) {
        requestBody.elcId = elcId;
      }

      if (values.subPillarId) {
        requestBody.subPillarId = values.subPillarId;
      }
      if (values.levelId) {
        requestBody.levelId = values.levelId;
      }

      await addCustomTactic(requestBody).unwrap();

      showToastNotification({
        type: ENotificationType.SUCCESS,
        message: 'Tactic added successfully!',
      });

      await onAdd(values);
    } catch (error: any) {
      if (
        error?.status === 400 &&
        error?.data?.message?.includes('already exist')
      ) {
        const errorMessage =
          'This tactic title already exists. Please choose a different name.';

        setTacticNameError(errorMessage);
        form.setFields([{ name: 'tacticName', errors: [errorMessage] }]);
        showToastNotification({
          type: ENotificationType.ERROR,
          message: errorMessage,
        });
        return;
      }

      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Failed to add tactic. Please try again.';

      showToastNotification({
        type: ENotificationType.ERROR,
        message: errorMessage,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTacticNameError(null);
    setSelectedPillarId(undefined);
    setSelectedSubPillarId(undefined);
    onClose();
  };

  const handleTacticNameChange = () => {
    if (tacticNameError) {
      setTacticNameError(null);
      form.setFields([{ name: 'tacticName', errors: [] }]);
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTacticNameError(null);
      setSelectedPillarId(undefined);
      setSelectedSubPillarId(undefined);
    }
  }, [open, form]);

  return (
    <StyledModal
      title="Add Tactic"
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      closeIcon={<span>×</span>}
      width={600}
    >
      <FormContainer>
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          onValuesChange={changed => {
            if ('pillarId' in changed) {
              setSelectedPillarId(changed.pillarId);
            }
            if ('subPillarId' in changed) {
              setSelectedSubPillarId(changed.subPillarId);
            }
          }}
        >
          <FormItem
            label="Tactic Name"
            name="tacticName"
            rules={[
              { required: true, message: 'Please enter tactic name' },
              { whitespace: true, message: 'Tactic name cannot be empty' },
            ]}
          >
            <Input
              placeholder="Enter Tactic Name"
              autoFocus
              onChange={handleTacticNameChange}
              data-cy="task_title_input"
            />
          </FormItem>

          <FormItem
            label="Pillar"
            name="pillarId"
            rules={[{ required: true, message: 'Please select a pillar' }]}
          >
            <Select
              placeholder="Select Pillar"
              options={pillarsOptions}
              showSearch
              optionFilterProp="label"
              onChange={handlePillarChange}
              data-cy="task_pillar_select"
            />
          </FormItem>

          <GoalLevelFields
            pillarsOfStrength={pillarsOfStrength}
            selectedPillarId={selectedPillarId}
            selectedSubPillarId={selectedSubPillarId}
            onGoalChange={handleSubPillarChange}
            goalDataCy="task_goal_select"
            levelDataCy="task_level_select"
          />

          {!elcId && (
            <FormItem
              name="scope"
              style={{ marginBottom: 0 }}
              rules={[
                { required: true, message: 'Please select a scope option' },
              ]}
            >
              <RadioGroup
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <Radio value="ALL_ELCS" data-cy="all_elcs_radio">
                  Include all ELC PMs
                </Radio>
                <Radio value="USER_LEAD_ELCS" data-cy="my_elcs_radio">
                  Include my ELC PMs only
                </Radio>
              </RadioGroup>
            </FormItem>
          )}
        </Form>

        <ModalFooter>
          <CancelButton
            onClick={handleCancel}
            color="primary"
            disabled={isLoading}
            data-cy="cancel_btn"
          >
            Cancel
          </CancelButton>
          <PrimaryButton
            type="primary"
            onClick={handleSubmit}
            color="primary"
            loading={isLoading}
            disabled={pillarsOfStrengthError}
            data-cy="task_save_btn"
          >
            Add
          </PrimaryButton>
        </ModalFooter>
      </FormContainer>
    </StyledModal>
  );
};

export default AddTacticModal;
